import AddTool, { AddToolContext } from './add-tool.js';
import ParametricPartView from '../../../views/parametric-part-view.js';
import SectionView from '../../../views/section-view.js';
import Signal from '../../../control-flow/signal.js';
import logging from '../../../logging.js';
import { Group, ParametricPart } from '../../../domain.js';
import { PI } from '../../../math/constants.js';
import { clamp } from '../../../math/clamp.js';
import { getParentView, getParentViewOrNull } from '../../../controllers/base-view-controllers.js';
import { overlapsSectionContent } from '../../../view3d/overlaps-section-view.js';
import { round2slot, slotAbove, slotBelow } from '../../../measurement.js';

const log = logging('tools');

export class AddParametricPartTool extends AddTool
{

    /**
     * This is the section that the mouse is currently over.
     */
    target?: SectionView;

    model: ParametricPart;
    proxy: ParametricPartView;

    declare onSuccess: Signal<[ParametricPart]>;

    constructor(part: ParametricPart, icon: HTMLElement, ctx: AddToolContext) {
        super(icon, ctx);

        this.model = this.ctx.defaults.get(ParametricPart, {
            name: part.name,
            width: 24,
            height: part.height,
            depth: 14,
            parameters: part.parameters,
        }) as ParametricPart;

        const
            rotation = part.name.includes('Light')
                ? { x: -PI/10, y: -PI/20, z: 0 }
                : { x: PI/10, y: PI/20, z: 0 },
            group = Group.fromJSON({ rotation });
        group.parametricParts.push(this.model);
        group.commit(true);

        this.updateThumbnail(group);
    }

    dragenter(): void {
        log.debug('AddParametricPartTool.dragenter()');
        this.proxy = new ParametricPartView(this.model);
    }

    dragover(event: PointerEvent, target: HTMLElement): void {
        log.debug('AddParametricPartTool.dragover()');

        this.updateWorldPosition(event, target);
        this.onDisplayChange.trigger();

        const y = this.worldPosition.y;

        const
            hit = this.ctx.picker.topHit,
            view = hit ? getParentViewOrNull(hit) as SectionView : null;
        if(hit?.userData.isSectionHit
           && !overlapsSectionContent(view, y, this.model.height || 32/25.4, this.proxy))
        {

            // Preventing the default here indicates that this is a valid drop
            // zone. Without doing this, the browser does not give us a `drop`
            // event, only a dragend().
            event.preventDefault();
            const
                target: SectionView = this.target = getParentView(hit) as SectionView,
                min = slotAbove(target.model.position.y),
                max = slotBelow(target.model.position.y + target.model.height),
                slot = clamp(round2slot(this.worldPosition.y), min, max);

            target.add(this.proxy);

            this.model.position.x = 0;
            this.model.position.y = slot;
            this.model.position.z = 25;
            this.model.width = target.model.width;
        }
        else {
            delete this.target;

            // check if the proxy belongs to the scene ... if so, fine, else
            // remake the proxy and add it back to the scene.
            if(this.proxy.parent != this.ctx.scene) {
                this.proxy.removeFromParent();
                this.ctx.scene.add(this.proxy);
            }

            this.model.position.x = this.worldPosition.x - 6;
            this.model.position.y = this.worldPosition.y;
            this.model.position.z = 25;
            this.model.width = 24;
        }
    }

    dragleave(): void {
        log.debug('AddParametricPartTool.dragleave()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();
    }

    cancel(): void {
        log.debug('AddParametricPartTool.cancel()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();
    }

    end(): void {
        log.debug('AddParametricPartTool.end()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();

        // if we ended over a section, then we'll have a defined target
        // property, which means we can drop something.
        if(this.target) {
            const model = this.model.clone(true);
            model.parent = this.target.model;
            model.position.z = 0;
            this.target.model.parametricParts.push(model);

            this.onSuccess.trigger(model);
        }
    }

}
export default AddParametricPartTool;
